import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import AdminLoginView from "@/pages/admin/AdminLoginView";
import AdminDashboardView from "@/pages/admin/AdminDashboardView";
import AdminDialogs from "@/pages/admin/AdminDialogs";

export default function AdminPanel() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ username: "", password: "" });

  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState("");
  const [createMode, setCreateMode] = useState(false);
  const [newDocData, setNewDocData] = useState("{\n  \n}");
  const [searchQuery, setSearchQuery] = useState("");

  const [viewMode, setViewMode] = useState("table"); // table or json

  // Coach Creation State
  const [createCoachMode, setCreateCoachMode] = useState(false);
  const [newCoachData, setNewCoachData] = useState({ name: "", email: "", password: "" });

  // Bulk Import State
  const [importMode, setImportMode] = useState(false);
  const [coachList, setCoachList] = useState([]);
  const [selectedCoachId, setSelectedCoachId] = useState("");
  const [centersList, setCentersList] = useState([]);
  const [selectedCenterForBatch, setSelectedCenterForBatch] = useState("");
  const [centerBatches, setCenterBatches] = useState([]);
  const [newCenterData, setNewCenterData] = useState({ name: "", location: "" });
  const [newBatchName, setNewBatchName] = useState("");
  const [unassignedStudents, setUnassignedStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [bulkMappingData, setBulkMappingData] = useState({ centerId: "", batchId: "" });
  const [mappingBatches, setMappingBatches] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [userActivity, setUserActivity] = useState([]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsLoggedIn(false);
    setCollections([]);
    setSelectedCollection(null);
    setDocuments([]);
    setCentersList([]);
    setCenterBatches([]);
    setUnassignedStudents([]);
    setSelectedStudentIds([]);
    toast.success("Logged out");
  };

  const getAdminApi = () => {
    const token = localStorage.getItem('adminToken');
    return {
      headers: { Authorization: `Bearer ${token}` }
    };
  };

  const fetchCollections = useCallback(async () => {
    try {
      const response = await api.get("/admin/collections", getAdminApi());
      setCollections(response.data.collections);
    } catch (error) {
      toast.error("Failed to fetch collections");
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  }, []);

  const fetchCenterBatches = useCallback(async (centerId) => {
    if (!centerId) {
      setCenterBatches([]);
      return;
    }

    try {
      const response = await api.get(`/admin/centers/${centerId}/batches`, getAdminApi());
      setCenterBatches(response.data);
    } catch (error) {
      toast.error("Failed to fetch batches");
    }
  }, []);

  const fetchMappingBatches = useCallback(async (centerId) => {
    if (!centerId) {
      setMappingBatches([]);
      return;
    }

    try {
      const response = await api.get(`/admin/centers/${centerId}/batches`, getAdminApi());
      setMappingBatches(response.data);
    } catch (error) {
      toast.error("Failed to fetch mapping batches");
    }
  }, []);

  const fetchUnassignedStudents = useCallback(async () => {
    try {
      const response = await api.get("/admin/students/unassigned", getAdminApi());
      setUnassignedStudents(response.data);
    } catch (error) {
      toast.error("Failed to fetch unassigned students");
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await api.get("/admin/analytics", getAdminApi());
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to fetch analytics");
    }
  }, []);

  const fetchUserActivity = useCallback(async () => {
    try {
      const response = await api.get("/admin/users/activity", getAdminApi());
      setUserActivity(response.data.users || []);
    } catch (error) {
      toast.error("Failed to fetch user activity");
    }
  }, []);

  const fetchCenters = useCallback(async () => {
    try {
      const response = await api.get("/admin/centers", getAdminApi());
      setCentersList(response.data);

      if (!selectedCenterForBatch && response.data.length > 0) {
        const initialCenterId = response.data[0].id;
        setSelectedCenterForBatch(initialCenterId);
        fetchCenterBatches(initialCenterId);
      }

      if (!bulkMappingData.centerId && response.data.length > 0) {
        const initialMappingCenterId = response.data[0].id;
        setBulkMappingData((current) => ({ ...current, centerId: initialMappingCenterId, batchId: "" }));
        fetchMappingBatches(initialMappingCenterId);
      }
    } catch (error) {
      toast.error("Failed to fetch centers");
    }
  }, [bulkMappingData.centerId, selectedCenterForBatch, fetchCenterBatches, fetchMappingBatches]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsLoggedIn(true);
      fetchCollections();
      fetchCenters();
      fetchUnassignedStudents();
      fetchAnalytics();
      fetchUserActivity();
    }
  }, [fetchAnalytics, fetchCenters, fetchCollections, fetchUnassignedStudents, fetchUserActivity]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post("/admin/login", loginData);
      localStorage.setItem('adminToken', response.data.token);
      setIsLoggedIn(true);
      toast.success("Admin login successful");
      fetchCollections();
      fetchCenters();
      fetchUnassignedStudents();
      fetchAnalytics();
      fetchUserActivity();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (collectionName) => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/collections/${collectionName}`, getAdminApi());
      setDocuments(response.data.documents);
      setTotalDocs(response.data.total);
      setSelectedCollection(collectionName);
    } catch (error) {
      toast.error("Failed to fetch documents");
    } finally {
      setLoading(false);
    }
  };

  const fetchCoaches = async () => {
    try {
      const response = await api.post("/admin/query/users", { role: "coach" }, getAdminApi());
      setCoachList(response.data.results);
    } catch (error) {
      toast.error("Failed to fetch coaches");
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!selectedCoachId) {
      toast.error("Please select a coach first");
      e.target.value = ''; // Reset
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const lines = text.split('\n');
      const students = [];

      // Skip header row
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const parts = line.split(',');
        if (parts.length >= 2) {
          const name = parts[0]?.trim();
          const dob = parts[1]?.trim();
          const parentName = parts[2]?.trim();
          const parentEmail = parts[3]?.trim();

          if (name && dob) {
            const studentObj = {
              name: name,
              dob: dob
            };
            if (parentName) studentObj.parent_name = parentName;
            if (parentEmail) studentObj.parent_email = parentEmail;

            students.push(studentObj);
          }
        }
      }

      if (students.length === 0) {
        toast.error("No valid students found in CSV");
        return;
      }

      try {
        setLoading(true);
        // Pass coach_id as query param
        const response = await api.post(`/students/bulk?coach_id=${selectedCoachId}`, students, getAdminApi());
        toast.success(`Imported ${response.data.success} students. Failed: ${response.data.failed}`);
        if (response.data.errors.length > 0) {
          console.error("Import errors:", response.data.errors);
          toast.warning("Some records failed. Check console for details.");
        }
        setImportMode(false);
        // Refresh if viewing students
        if (selectedCollection === 'students') fetchDocuments('students');
      } catch (error) {
        toast.error("Failed to import students");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleCreateCenter = async (e) => {
    e.preventDefault();
    if (!newCenterData.name.trim()) {
      return;
    }

    try {
      await api.post("/admin/centers", newCenterData, getAdminApi());
      toast.success("Center created successfully");
      setNewCenterData({ name: "", location: "" });
      fetchCenters();
      fetchCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create center");
    }
  };

  const handleCreateBatch = async (e) => {
    e.preventDefault();
    if (!selectedCenterForBatch || !newBatchName.trim()) {
      toast.error("Select a center and enter a batch name");
      return;
    }

    try {
      await api.post(`/admin/centers/${selectedCenterForBatch}/batches`, { name: newBatchName }, getAdminApi());
      toast.success("Batch created successfully");
      setNewBatchName("");
      fetchCenterBatches(selectedCenterForBatch);
      fetchMappingBatches(bulkMappingData.centerId || selectedCenterForBatch);
      fetchCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create batch");
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudentIds((current) =>
      current.includes(studentId)
        ? current.filter((id) => id !== studentId)
        : [...current, studentId]
    );
  };

  const handleSelectAllUnassigned = () => {
    if (selectedStudentIds.length === unassignedStudents.length) {
      setSelectedStudentIds([]);
      return;
    }

    setSelectedStudentIds(unassignedStudents.map((student) => student.id));
  };

  const handleBulkAssignStudents = async () => {
    if (!selectedStudentIds.length) {
      toast.error("Select at least one student");
      return;
    }

    if (!bulkMappingData.centerId || !bulkMappingData.batchId) {
      toast.error("Select a target center and batch");
      return;
    }

    try {
      const response = await api.post(
        "/admin/students/bulk-assign",
        {
          student_ids: selectedStudentIds,
          center_id: bulkMappingData.centerId,
          batch_id: bulkMappingData.batchId,
        },
        getAdminApi()
      );
      toast.success(`Mapped ${response.data.updated} students successfully`);
      setSelectedStudentIds([]);
      fetchUnassignedStudents();
      fetchCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to map students");
    }
  };

  const handleViewDoc = (doc) => {
    setSelectedDoc(doc);
    setEditData(JSON.stringify(doc, null, 2));
    setEditMode(false);
  };

  const handleEditDoc = () => {
    setEditMode(true);
  };

  const handleSaveDoc = async () => {
    try {
      const parsedData = JSON.parse(editData);
      await api.put(`/admin/collections/${selectedCollection}/${selectedDoc.id}`,
        { data: parsedData },
        getAdminApi()
      );
      toast.success("Document updated");
      setSelectedDoc(null);
      setEditMode(false);
      fetchDocuments(selectedCollection);
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON format");
      } else {
        toast.error(error.response?.data?.detail || "Failed to update document");
      }
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    try {
      await api.delete(`/admin/collections/${selectedCollection}/${docId}`, getAdminApi());
      toast.success("Document deleted");
      fetchDocuments(selectedCollection);
      setSelectedDoc(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete document");
    }
  };

  const handleCreateDoc = async () => {
    try {
      const parsedData = JSON.parse(newDocData);
      await api.post(`/admin/collections/${selectedCollection}`,
        { data: parsedData },
        getAdminApi()
      );
      toast.success("Document created");
      setCreateMode(false);
      setNewDocData("{\n  \n}");
      fetchDocuments(selectedCollection);
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON format");
      } else {
        toast.error(error.response?.data?.detail || "Failed to create document");
      }
    }
  };

  const handleCreateCoach = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/coaches", newCoachData, getAdminApi());
      toast.success("Coach account created successfully");
      setCreateCoachMode(false);
      setNewCoachData({ name: "", email: "", password: "" });
      // Optionally refresh collections or users if we were viewing them, but simple toast is enough for now
      if (selectedCollection === 'users') fetchDocuments('users');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create coach");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = prompt("Enter new temporary password for this user:");
    if (!newPassword) return;

    try {
      await api.post(`/admin/users/${userId}/reset-password`,
        { password: newPassword },
        getAdminApi()
      );
      toast.success("Password reset successfully. User will be prompted to change it.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const getFilteredDocs = () => {
    if (!searchQuery) return documents;
    return documents.filter(doc =>
      JSON.stringify(doc).toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const getDocKeys = () => {
    if (documents.length === 0) return [];
    const allKeys = new Set();
    documents.forEach(doc => Object.keys(doc).forEach(key => allKeys.add(key)));
    return Array.from(allKeys);
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return <span className="text-slate-400">null</span>;
    if (typeof value === 'object') return <span className="text-purple-600 text-xs">{JSON.stringify(value)}</span>;
    if (typeof value === 'boolean') return <span className={value ? "text-green-600" : "text-red-600"}>{value.toString()}</span>;
    if (typeof value === 'string' && value.length > 50) return value.substring(0, 50) + "...";
    return value;
  };

  if (!isLoggedIn) {
    return <AdminLoginView loading={loading} loginData={loginData} setLoginData={setLoginData} handleLogin={handleLogin} />;
  }

  return (
    <>
      <AdminDashboardView
        navigate={navigate}
        fetchCollections={fetchCollections}
        fetchCenters={fetchCenters}
        fetchUnassignedStudents={fetchUnassignedStudents}
        fetchAnalytics={fetchAnalytics}
        fetchUserActivity={fetchUserActivity}
        setCreateCoachMode={setCreateCoachMode}
        fetchCoaches={fetchCoaches}
        setImportMode={setImportMode}
        handleLogout={handleLogout}
        newCenterData={newCenterData}
        setNewCenterData={setNewCenterData}
        handleCreateCenter={handleCreateCenter}
        centersList={centersList}
        selectedCenterForBatch={selectedCenterForBatch}
        setSelectedCenterForBatch={setSelectedCenterForBatch}
        fetchCenterBatches={fetchCenterBatches}
        newBatchName={newBatchName}
        setNewBatchName={setNewBatchName}
        handleCreateBatch={handleCreateBatch}
        centerBatches={centerBatches}
        bulkMappingData={bulkMappingData}
        setBulkMappingData={setBulkMappingData}
        fetchMappingBatches={fetchMappingBatches}
        mappingBatches={mappingBatches}
        selectedStudentIds={selectedStudentIds}
        unassignedStudents={unassignedStudents}
        handleSelectAllUnassigned={handleSelectAllUnassigned}
        toggleStudentSelection={toggleStudentSelection}
        handleBulkAssignStudents={handleBulkAssignStudents}
        collections={collections}
        selectedCollection={selectedCollection}
        setSelectedCollection={setSelectedCollection}
        fetchDocuments={fetchDocuments}
        totalDocs={totalDocs}
        viewMode={viewMode}
        setViewMode={setViewMode}
        createMode={createMode}
        setCreateMode={setCreateMode}
        newDocData={newDocData}
        setNewDocData={setNewDocData}
        handleCreateDoc={handleCreateDoc}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        loading={loading}
        documents={documents}
        getDocKeys={getDocKeys}
        getFilteredDocs={getFilteredDocs}
        formatValue={formatValue}
        handleResetPassword={handleResetPassword}
        handleViewDoc={handleViewDoc}
        handleDeleteDoc={handleDeleteDoc}
        analytics={analytics}
        userActivity={userActivity}
      />

      <AdminDialogs
        selectedDoc={selectedDoc}
        setSelectedDoc={setSelectedDoc}
        editMode={editMode}
        setEditMode={setEditMode}
        editData={editData}
        setEditData={setEditData}
        handleEditDoc={handleEditDoc}
        handleSaveDoc={handleSaveDoc}
        handleDeleteDoc={handleDeleteDoc}
        createCoachMode={createCoachMode}
        setCreateCoachMode={setCreateCoachMode}
        newCoachData={newCoachData}
        setNewCoachData={setNewCoachData}
        handleCreateCoach={handleCreateCoach}
        loading={loading}
        importMode={importMode}
        setImportMode={setImportMode}
        selectedCoachId={selectedCoachId}
        setSelectedCoachId={setSelectedCoachId}
        coachList={coachList}
        handleBulkUpload={handleBulkUpload}
      />
    </>
  );
}
